import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Menu } from "@headlessui/react";
import {
  ChevronDownIcon,
  UserIcon,
  ViewColumnsIcon,
  ArrowRightOnRectangleIcon,
  AcademicCapIcon,
  Cog6ToothIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  BookOpenIcon,
  Squares2X2Icon,
  PlayIcon,
  VideoCameraIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import logoWhite from "../images/logo-white.png";
import menuBackground from "../images/menu-background.png";

function TopBar({ onLogout, userType = "user", userEmail = "" }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const mobileButtonRef = useRef(null);

  // Close mobile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        mobileButtonRef.current &&
        !mobileButtonRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close mobile menu on escape key
  useEffect(() => {
    function handleEscapeKey(event) {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    }

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  // Common class for all navigation items
  const navItemClass =
    "text-white font-semibold rounded px-3 py-2 cursor-pointer hover:bg-primary-600 hover:bg-opacity-70 transition-colors";

  const renderNavigationItems = () => {
    return (
      <>
        <Link to="/lessons" className={navItemClass}>
          Lessons
        </Link>
        <Link to="/videos" className={navItemClass}>
          Videos
        </Link>
        <Link to="/topics" className={navItemClass}>
          Topics
        </Link>
        <Link to="/gallery" className={navItemClass}>
          Gallery
        </Link>
      </>
    );
  };

  const renderMobileNavigationItems = () => {
    return (
      <>
        <Link
          to="/lessons"
          className="block w-full text-white font-semibold py-3 px-4 hover:bg-primary-600 hover:bg-opacity-70 transition-colors border-b border-primary-600 border-opacity-30 flex items-center space-x-3"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <BookOpenIcon className="w-5 h-5" />
          <span>Lessons</span>
        </Link>
        <Link
          to="/videos"
          className="block w-full text-white font-semibold py-3 px-4 hover:bg-primary-600 hover:bg-opacity-70 transition-colors border-b border-primary-600 border-opacity-30 flex items-center space-x-3"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <PlayIcon className="w-5 h-5" />
          <span>Videos</span>
        </Link>
        <Link
          to="/topics"
          className="block w-full text-white font-semibold py-3 px-4 hover:bg-primary-600 hover:bg-opacity-70 transition-colors border-b border-primary-600 border-opacity-30 flex items-center space-x-3"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Squares2X2Icon className="w-5 h-5" />
          <span>Topics</span>
        </Link>
        <Link
          to="/gallery"
          className="block w-full text-white font-semibold py-3 px-4 hover:bg-primary-600 hover:bg-opacity-70 transition-colors border-b border-primary-600 border-opacity-30 flex items-center space-x-3"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <SparklesIcon className="w-5 h-5" />
          <span>Gallery</span>
        </Link>
      </>
    );
  };

  return (
    <div
      className="w-full h-20 bg-cover bg-center flex items-center justify-between px-4 sm:px-6 relative"
      style={{
        backgroundImage: `url(${menuBackground})`,
      }}
    >
      {/* Logo - Left justified */}
      <Link to="/" className="flex items-center cursor-pointer">
        <img
          src={logoWhite}
          alt="KUZKO HISTORY"
          className="h-8 w-auto sm:h-10 md:h-12"
        />
      </Link>

      {/* Desktop Navigation - Hidden on mobile */}
      <div className="hidden md:flex items-center space-x-3">
        {renderNavigationItems()}

        {/* Profile dropdown */}
        <Menu as="div" className="relative">
          <Menu.Button
            className={`${navItemClass} flex items-center space-x-1`}
          >
            <span>Profile</span>
            <ChevronDownIcon className="w-4 h-4" />
          </Menu.Button>

          <Menu.Items
            className="absolute right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
            style={{
              minWidth: "280px",
              width: "max-content",
              maxWidth: "400px",
            }}
          >
            {/* User email */}
            <Menu.Item>
              {({ active }) => (
                <div className="px-4 py-2 flex items-center space-x-3">
                  <UserIcon className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-900">{userEmail}</span>
                </div>
              )}
            </Menu.Item>

            {/* Admin Dashboard */}
            {userType === "admin" && (
              <>
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/admin-dashboard"
                      className={`${
                        active ? "bg-gray-100" : ""
                      } w-full px-4 py-2 text-left flex items-center space-x-3 hover:text-primary-700 transition-colors`}
                    >
                      <ViewColumnsIcon className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-900 hover:text-primary-700 transition-colors">
                        Dashboard
                      </span>
                    </Link>
                  )}
                </Menu.Item>
              </>
            )}

            {/* Super Admin Navigation Items */}
            {userType === "super_admin" && (
              <>
                <div className="border-t border-gray-200 my-1"></div>
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/admin/lessons"
                      className={`${
                        active ? "bg-gray-100" : ""
                      } w-full px-4 py-2 text-left flex items-center space-x-3 hover:text-primary-700 transition-colors`}
                    >
                      <AcademicCapIcon className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-900 hover:text-primary-700 transition-colors">
                        Lessons
                      </span>
                    </Link>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/admin/videos"
                      className={`${
                        active ? "bg-gray-100" : ""
                      } w-full px-4 py-2 text-left flex items-center space-x-3 hover:text-primary-700 transition-colors`}
                    >
                      <VideoCameraIcon className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-900 hover:text-primary-700 transition-colors">
                        Videos
                      </span>
                    </Link>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/admin/settings"
                      className={`${
                        active ? "bg-gray-100" : ""
                      } w-full px-4 py-2 text-left flex items-center space-x-3 hover:text-primary-700 transition-colors`}
                    >
                      <Cog6ToothIcon className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-900 hover:text-primary-700 transition-colors">
                        Settings
                      </span>
                    </Link>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/admin/users"
                      className={`${
                        active ? "bg-gray-100" : ""
                      } w-full px-4 py-2 text-left flex items-center space-x-3 hover:text-primary-700 transition-colors`}
                    >
                      <UsersIcon className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-900 hover:text-primary-700 transition-colors">
                        Users
                      </span>
                    </Link>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/admin/organizations"
                      className={`${
                        active ? "bg-gray-100" : ""
                      } w-full px-4 py-2 text-left flex items-center space-x-3 hover:text-primary-700 transition-colors`}
                    >
                      <BuildingOfficeIcon className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-900 hover:text-primary-700 transition-colors">
                        Organizations
                      </span>
                    </Link>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/admin/stats"
                      className={`${
                        active ? "bg-gray-100" : ""
                      } w-full px-4 py-2 text-left flex items-center space-x-3 hover:text-primary-700 transition-colors`}
                    >
                      <ChartBarIcon className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-900 hover:text-primary-700 transition-colors">
                        Stats
                      </span>
                    </Link>
                  )}
                </Menu.Item>
              </>
            )}

            {/* Divider */}
            <div className="border-t border-gray-200 my-1"></div>

            {/* Log Out */}
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={onLogout}
                  className={`${
                    active ? "bg-gray-100" : ""
                  } w-full px-4 py-2 text-left flex items-center space-x-3 hover:text-primary-700 transition-colors`}
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5" />
                  <span>Log Out</span>
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Menu>
      </div>

      {/* Mobile Menu Button */}
      <button
        ref={mobileButtonRef}
        className="md:hidden text-white p-2 hover:bg-primary-600 hover:bg-opacity-70 rounded transition-colors"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle mobile menu"
      >
        {isMobileMenuOpen ? (
          <XMarkIcon className="w-6 h-6" />
        ) : (
          <Bars3Icon className="w-6 h-6" />
        )}
      </button>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="absolute top-20 left-0 right-0 bg-primary-700 bg-opacity-95 backdrop-blur-sm z-50 md:hidden animate-in slide-in-from-top-2 duration-200"
        >
          <div className="py-4">
            {renderMobileNavigationItems()}

            {/* Mobile Profile Section */}
            <div className="px-4 py-3 border-b border-primary-600 border-opacity-30">
              <div className="flex items-center space-x-3 text-white">
                <UserIcon className="w-5 h-5" />
                <span className="font-semibold">{userEmail}</span>
              </div>
            </div>

            {/* Mobile Admin Options */}
            {userType === "admin" && (
              <Link
                to="/admin-dashboard"
                className="block w-full text-white font-semibold py-3 px-4 hover:bg-primary-600 hover:bg-opacity-70 transition-colors border-b border-primary-600 border-opacity-30"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Dashboard
              </Link>
            )}

            {userType === "super_admin" && (
              <>
                <Link
                  to="/admin/lessons"
                  className="block w-full text-white font-semibold py-3 px-4 hover:bg-primary-600 hover:bg-opacity-70 transition-colors border-b border-primary-600 border-opacity-30"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Lessons
                </Link>
                <Link
                  to="/admin/videos"
                  className="block w-full text-white font-semibold py-3 px-4 hover:bg-primary-600 hover:bg-opacity-70 transition-colors border-b border-primary-600 border-opacity-30 flex items-center space-x-3"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <VideoCameraIcon className="w-5 h-5" />
                  <span>Videos</span>
                </Link>
                <Link
                  to="/admin/settings"
                  className="block w-full text-white font-semibold py-3 px-4 hover:bg-primary-600 hover:bg-opacity-70 transition-colors border-b border-primary-600 border-opacity-30"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Settings
                </Link>
                <Link
                  to="/admin/users"
                  className="block w-full text-white font-semibold py-3 px-4 hover:bg-primary-600 hover:bg-opacity-70 transition-colors border-b border-primary-600 border-opacity-30"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Users
                </Link>
                <Link
                  to="/admin/organizations"
                  className="block w-full text-white font-semibold py-3 px-4 hover:bg-primary-600 hover:bg-opacity-70 transition-colors border-b border-primary-600 border-opacity-30"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Organizations
                </Link>
                <Link
                  to="/admin/stats"
                  className="block w-full text-white font-semibold py-3 px-4 hover:bg-primary-600 hover:bg-opacity-70 transition-colors border-b border-primary-600 border-opacity-30"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Stats
                </Link>
              </>
            )}

            {/* Mobile Logout */}
            <button
              onClick={() => {
                onLogout();
                setIsMobileMenuOpen(false);
              }}
              className="w-full text-left text-white font-semibold py-3 px-4 hover:bg-primary-600 hover:bg-opacity-70 transition-colors flex items-center space-x-3"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TopBar;
